import { authedFetch } from '../lib/authedFetch';
import { economicScreenRequestFromSite } from '../lib/economicContribution/siteScreeningInput';
import { resolveFipsFromRefdata, type RefdataFipsResult } from './refdata/fipsLookup';
import type { SiteEntity } from '../types/site';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import type {
  EconomicContributionScreenRequest,
  EconomicContributionScreeningSnapshot,
  EconomicContributionSiteInputs,
  EconomicMetricObservation,
  EconomicProfileSnapshot,
  EconomicWorkforceProfileRequest,
  EconomicWorkforceProfileResponse,
} from '../types/economicContribution';

const ECONOMIC_WORKFORCE_PROFILE_ROUTE = '/api/economic-contribution/workforce-profile';
const ECONOMIC_PROFILE_METRICS = new Set<EconomicMetricObservation['metric']>([
  'establishments',
  'employment',
  'annual-average-wage',
  'median-hourly-wage',
  'labor-force',
  'unemployment-rate',
  'population',
  'median-household-income',
  'gross-domestic-product',
  'personal-income',
]);
const ECONOMIC_PROFILE_DATASETS = new Set<EconomicMetricObservation['sourceDataset']>([
  'QCEW',
  'OEWS',
  'LAUS',
  'CBP',
  'ACS',
  'BEA',
  'QWI',
]);
const ECONOMIC_PROFILE_GEOGRAPHIES = new Set<EconomicMetricObservation['geography']['kind']>([
  'county',
  'metro',
  'nonmetro',
  'state',
  'national',
]);

function object(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

async function responseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try { return JSON.parse(text) as unknown; } catch { return { error: text }; }
}

function stringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function labelledCode(value: unknown): boolean {
  const candidate = object(value);
  return Boolean(candidate && typeof candidate.code === 'string' && typeof candidate.label === 'string');
}

function economicObservation(value: unknown): value is EconomicMetricObservation {
  const candidate = object(value);
  const geography = object(candidate?.geography);
  return Boolean(
    candidate
    && typeof candidate.metric === 'string'
    && ECONOMIC_PROFILE_METRICS.has(candidate.metric as EconomicMetricObservation['metric'])
    && typeof candidate.value === 'number'
    && Number.isFinite(candidate.value)
    && typeof candidate.units === 'string'
    && geography
    && typeof geography.kind === 'string'
    && ECONOMIC_PROFILE_GEOGRAPHIES.has(geography.kind as EconomicMetricObservation['geography']['kind'])
    && typeof geography.code === 'string'
    && typeof geography.label === 'string'
    && (candidate.industry === undefined || labelledCode(candidate.industry))
    && (candidate.occupation === undefined || labelledCode(candidate.occupation))
    && typeof candidate.period === 'string'
    && typeof candidate.sourceDataset === 'string'
    && ECONOMIC_PROFILE_DATASETS.has(candidate.sourceDataset as EconomicMetricObservation['sourceDataset'])
    && typeof candidate.sourceUrl === 'string'
    && candidate.suppressed === false
    && Number.isInteger(candidate.fallbackLevel)
    && Number(candidate.fallbackLevel) >= 1
    && Number(candidate.fallbackLevel) <= 5
  );
}

function economicProfileSnapshot(value: unknown): value is EconomicProfileSnapshot {
  const candidate = object(value);
  const sourceVintages = object(candidate?.sourceVintages);
  return Boolean(
    candidate
    && candidate.schemaVersion === 1
    && typeof candidate.snapshotId === 'string'
    && candidate.snapshotId.length > 0
    && typeof candidate.countyFips === 'string'
    && /^\d{5}$/.test(candidate.countyFips)
    && typeof candidate.stateFips === 'string'
    && /^\d{2}$/.test(candidate.stateFips)
    && typeof candidate.stateCode === 'string'
    && /^[A-Z]{2}$/.test(candidate.stateCode)
    && typeof candidate.countyName === 'string'
    && candidate.countyName.length > 0
    && typeof candidate.generatedAt === 'string'
    && Number.isFinite(Date.parse(candidate.generatedAt))
    && Array.isArray(candidate.observations)
    && candidate.observations.every(economicObservation)
    && stringArray(candidate.missingMetrics)
    && stringArray(candidate.providerErrors)
    && sourceVintages
    && Object.values(sourceVintages).every((entry) => typeof entry === 'string')
  );
}

/**
 * Hydrate public workforce context from canonical server-owned site facts.
 * This endpoint deliberately accepts no project MW or jobs-model inputs.
 */
export async function hydrateEconomicWorkforceProfile(
  request: EconomicWorkforceProfileRequest,
  options: { signal?: AbortSignal; fetcher?: typeof authedFetch } = {},
): Promise<EconomicWorkforceProfileResponse> {
  const response = await (options.fetcher ?? authedFetch)(ECONOMIC_WORKFORCE_PROFILE_ROUTE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal: options.signal,
  });
  const body = await responseBody(response);
  if (!response.ok) {
    const message = object(body)?.error;
    throw new Error(typeof message === 'string'
      ? message
      : `Economic workforce profile could not be loaded (${response.status}).`);
  }
  const result = object(body);
  if (!result || typeof result.unchanged !== 'boolean' || !economicProfileSnapshot(result.profile)) {
    throw new Error('The economic workforce profile service returned an invalid response.');
  }
  return {
    profile: result.profile,
    unchanged: result.unchanged,
  };
}

export async function screenEconomicContribution(
  input: EconomicContributionScreenRequest,
  options: {
    signal?: AbortSignal;
    fetcher?: typeof authedFetch;
    /** Lookup hint only; server-side site authorization remains authoritative. */
    ownerUid?: string;
  } = {},
): Promise<EconomicContributionScreeningSnapshot> {
  const route = options.ownerUid?.trim()
    ? `/api/economic-contribution/screen?ownerUid=${encodeURIComponent(options.ownerUid.trim())}`
    : '/api/economic-contribution/screen';
  const response = await (options.fetcher ?? authedFetch)(route, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Phase cases are composed by the server from the authorized site record.
    // Keep the request compatible with the existing strict single-screen API.
    body: JSON.stringify({ ...input, additionalScenarios: undefined }),
    signal: options.signal,
  });
  const body = await responseBody(response);
  if (!response.ok) {
    const responseObject = object(body);
    const message = responseObject?.error;
    const detail = responseObject?.detail;
    const base = typeof message === 'string' ? message : `Economic screen failed (${response.status}).`;
    throw new Error(typeof detail === 'string' && detail.trim() ? `${base} ${detail}` : base);
  }
  const result = object(body);
  if (
    result?.schemaVersion !== 1
    || typeof result.snapshotId !== 'string'
    || result.siteId !== input.directJobs.siteId
    || !object(result.location)
    || typeof result.inputHash !== 'string'
    || !object(result.directJobs)
    // P3.7 — the envelope never inspected the water screen it carries.
    || (result.waterUseScreening != null && !validWaterUseScreeningEnvelope(result.waterUseScreening))
  ) {
    throw new Error('The economic screening service returned an invalid snapshot.');
  }
  return body as EconomicContributionScreeningSnapshot;
}

/** A present water screen must at least carry a status and, when calculated, a scenario array. */
function validWaterUseScreeningEnvelope(value: unknown): boolean {
  const screen = object(value);
  if (!screen || typeof screen.status !== 'string') return false;
  return screen.status !== 'calculated' || Array.isArray(screen.scenarios);
}

/** Resolve FIPS from existing coordinates, then send only the minimal explicit refinements. */
export async function screenEconomicContributionForSite(
  site: SiteEntity,
  inputs: EconomicContributionSiteInputs,
  options: {
    signal?: AbortSignal;
    fetcher?: typeof authedFetch;
    resolveFips?: typeof resolveFipsFromRefdata;
    /** Already server-verified location identity, when workforce hydration resolved it. */
    resolvedFips?: RefdataFipsResult;
    /** Exact current SED used to project trusted local climate into the water screen. */
    sed?: SiteEvidenceDocument | null;
  } = {},
): Promise<EconomicContributionScreeningSnapshot> {
  const fips = options.resolvedFips
    ?? await (options.resolveFips ?? resolveFipsFromRefdata)(site.location.lat, site.location.lng);
  if (!fips) throw new Error('County FIPS could not be resolved for this site. Refresh nationwide county reference data and retry.');
  const request = economicScreenRequestFromSite(
    { ...site, economicContributionInputs: inputs },
    { sed: options.sed },
  );
  if (!request) throw new Error('Record a bounded project MW range or an exact positive MW value before running the economic screen.');
  const withLocation: EconomicContributionScreenRequest = {
    ...request,
    directJobs: {
      ...request.directJobs,
      location: {
        stateFips: fips.stateFips,
        countyFips: fips.countyFips,
        stateCode: fips.stateCode,
        countyName: fips.countyName,
      },
    },
  };
  return screenEconomicContribution(withLocation, {
    ...options,
    ...(site.createdBy?.uid ? { ownerUid: site.createdBy.uid } : {}),
  });
}
